import type { ReviewSourceId } from '@flexbyt/shared';
import {
    DIRECT_INTEGRATIONS,
    EXTENSION_INTEGRATIONS,
    integrationPath,
} from './integrations.js';
import { PLATFORM_CONTENT, SEO_PLATFORMS } from './platforms.js';
import { CONNECT_SOURCES } from './sources.js';
import { WIDGET_CONTENT, widgetPath } from './widgets.js';

/**
 * The site's information architecture, in one place.
 *
 * ---------------------------------------------------------------------------
 * Derived, not retyped
 * ---------------------------------------------------------------------------
 * The widget menu is built from `WIDGET_CONTENT` and the source links from
 * `CONNECT_SOURCES`, so adding a layout or a source puts it in the navigation
 * and the footer without anybody remembering to. A hand-written nav is where
 * "we shipped the Grid generator" and "the Grid is in the menu" become two
 * separate tasks, and the second one gets forgotten.
 *
 * It also means the nav inherits the guarantees the content already has —
 * including that Shopify cannot appear in it, because `WebsitePlatform` does
 * not contain Shopify and `PLATFORM_CONTENT` is exhaustive over that type.
 *
 * ---------------------------------------------------------------------------
 * Paths that do not exist yet
 * ---------------------------------------------------------------------------
 * Most of these routes are built by later phases. They are named here anyway,
 * because the navigation *is* the plan for the site — writing it out is how the
 * architecture gets checked before ten pages are built against it. Until each
 * one lands, the link reaches the 404 page, which says so.
 */

export type NavLink = {
  label: string;
  to: string;
  /** A second line in the menu, where the label alone is not enough. */
  description?: string;
};

export type NavGroup = {
  label: string;
  /** Direct link when there is no menu. */
  to?: string;
  links?: NavLink[];
  /**
   * Draw each item with a sketch of the layout it names.
   *
   * True for widgets and nothing else. "Masonry" and "Grid" are close to
   * meaningless as words — the difference between them is entirely a shape —
   * so that menu shows the shapes. Everywhere else a picture beside a link is
   * decoration, and the brief calls that out by name.
   */
  visual?: boolean;
};

/**
 * The three jobs, which are the product's whole positioning.
 *
 * First in the menu because they are first in the brief: a visitor should
 * understand ReviewsJet as import → manage → display, and the navigation is one
 * of the few places that runs in a fixed order on every page.
 *
 * ---------------------------------------------------------------------------
 * Why these point at three differently-shaped URLs
 * ---------------------------------------------------------------------------
 * They pointed at `/product/import`, `/product/manage` and `/product/display`
 * for six phases, and none of the three was ever built — the launch audit found
 * the whole Product menu leading to 404s.
 *
 * The fix is not to build them. `/product/import` would have competed with
 * `/integrations` for the same query, and `/product/display` with `/widgets`,
 * which is the duplicate-route cannibalisation the SEO brief warns about: two
 * pages of one site answering "put Google reviews on my website", splitting the
 * signal between them. Those two jobs already have a substantial page each.
 *
 * Manage was the one with no home anywhere on the site, so it got a page. The
 * labels carry the import → manage → display story; the URLs are the ones each
 * job should rank for.
 */
export const PRODUCT_LINKS: NavLink[] = [
  {
    label: 'Import',
    to: '/integrations',
    description: 'Bring reviews in from Google, Trustpilot, Yelp and more',
  },
  {
    label: 'Manage',
    to: '/manage-reviews',
    description: 'Every review in one library, published on your say-so',
  },
  {
    label: 'Display',
    to: '/widgets',
    description: 'Turn them into widgets that match your site',
  },
];

/**
 * The two pages organised by *who* rather than by how.
 *
 * Kept as their own array because the footer wants them under Solutions, where
 * they sit beside the website platforms — "is this for someone like me" is the
 * same kind of question as "does it work on Wix". The top navigation puts them
 * somewhere else; see `RESOURCE_LINKS`.
 */
export const AUDIENCE_LINKS: NavLink[] = [
  {
    label: 'Who it is for',
    to: '/who-its-for',
    description: 'Restaurants, hotels, trades, clinics, shops and more',
  },
  {
    label: 'For agencies',
    to: '/agencies',
    description: 'Client sites, on every platform, on the free plan',
  },
];

/**
 * Resources — and "Who it is for" belongs in here rather than in a menu of its
 * own.
 *
 * It briefly had its own top-level menu, which was wrong for a reason that only
 * shows up when you read the bar left to right. Product, Widgets and
 * Integrations are the product; Pricing is the transaction. Everything between
 * them is *reading you can do before deciding* — a blog post, a comparison, a
 * free tool, and now a page that tells you whether a business like yours is the
 * kind this is built for. That is one job, and one job is one menu.
 *
 * A seventh top-level item also cost more than it looked: the bar is the one
 * piece of furniture on every page, and every label added to it makes the other
 * six slightly harder to find.
 *
 * It leads the list because it is the only one of the four that answers a
 * question somebody has *before* they are interested — the blog, the tools and
 * the comparisons all assume you already are.
 */
export const RESOURCE_LINKS: NavLink[] = [
  {
    label: 'Who it is for',
    to: '/who-its-for',
    description: 'Restaurants, hotels, trades, clinics, shops and more',
  },
  { label: 'Blog', to: '/blog', description: 'Guides on collecting and showing reviews' },
  { label: 'Free tools', to: '/tools', description: 'Build a review widget without an account' },
  { label: 'Comparisons', to: '/compare', description: 'How ReviewsJet compares, honestly' },
];

/** The widget menu, with a sketch per layout. */
export const WIDGET_LINKS: NavLink[] = WIDGET_CONTENT.map((widget) => ({
  label: widget.seoName,
  to: widgetPath(widget),
  description: widget.bestFor,
}));

/**
 * The review sources, as a menu.
 *
 * Direct imports first and the extension group after, in one list but in that
 * order — the menu is too small a surface to carry the full explanation, and
 * ordering is the cheapest way to avoid implying the nine are interchangeable.
 * The page it leads to draws the distinction properly.
 */
export const INTEGRATION_LINKS: NavLink[] = [
  { label: 'All integrations', to: '/integrations' },
  ...DIRECT_INTEGRATIONS.map((entry) => ({
    label: entry.name,
    to: integrationPath(entry),
  })),
  ...EXTENSION_INTEGRATIONS.map((entry) => ({
    label: `${entry.name} (extension)`,
    to: integrationPath(entry),
  })),
];

export const PRIMARY_NAV: NavGroup[] = [
  { label: 'Product', links: PRODUCT_LINKS },
  { label: 'Widgets', links: WIDGET_LINKS, visual: true },
  { label: 'Integrations', links: INTEGRATION_LINKS },
  { label: 'For agencies', to: '/agencies' },
  { label: 'Resources', links: RESOURCE_LINKS },
  { label: 'Pricing', to: '/pricing' },
];

// ---------------------------------------------------------------------------
// Footer
// ---------------------------------------------------------------------------

export type FooterColumn = {
  heading: string;
  links: NavLink[];
};

/**
 * The five direct-import sources, in the order onboarding offers them.
 *
 * Pointed at the integration pages rather than composed by hand: those pages
 * are the source acquisition surface, and one definition of where they live
 * means the footer cannot drift from the navigation.
 */
export const SOURCE_LINKS: NavLink[] = DIRECT_INTEGRATIONS.map((entry) => ({
  label: entry.name,
  to: integrationPath(entry),
}));

/**
 * The website platforms worth a page of their own.
 *
 * `/for/<platform>` is the hub: it gathers "Google reviews for Wix",
 * "Trustpilot reviews for Wix" and the rest, and gives the source × platform
 * pages a parent to sit under in a breadcrumb trail. A footer link labelled
 * "Wix" needs somewhere honest to land, and pointing it straight at one
 * source's page would be picking a source on the visitor's behalf.
 */
export const PLATFORM_LINKS: NavLink[] = SEO_PLATFORMS.map((platform) => ({
  label: PLATFORM_CONTENT[platform].name,
  to: `/for/${PLATFORM_CONTENT[platform].slug}`,
}));

export const FOOTER_COLUMNS: FooterColumn[] = [
  {
    heading: 'Product',
    links: [
      { label: 'Import reviews', to: '/integrations' },
      { label: 'Manage reviews', to: '/manage-reviews' },
      { label: 'Display widgets', to: '/widgets' },
      // Secondary to the three jobs above — reviews you don't have yet,
      // rather than ones you already have — so it sits after them rather
      // than among them.
      { label: 'Collection forms', to: '/review-forms' },
      { label: 'Free tools', to: '/tools' },
      { label: 'Pricing', to: '/pricing' },
    ],
  },
  { heading: 'Review sources', links: SOURCE_LINKS },
  {
    heading: 'Widgets',
    // The four layouts people search for by name. The full set is one click
    // further on, and a footer that lists everything lists nothing.
    links: WIDGET_LINKS.slice(0, 4),
  },
  {
    heading: 'Solutions',
    links: [...AUDIENCE_LINKS, ...PLATFORM_LINKS],
  },
  { heading: 'Resources', links: RESOURCE_LINKS },
];

export const LEGAL_LINKS: NavLink[] = [
  { label: 'Privacy', to: '/privacy-policy' },
  { label: 'Terms', to: '/terms-of-service' },
  { label: 'Cookies', to: '/cookie-policy' },
];

/**
 * Sources that appear in the footer, for the test that checks the pairing.
 *
 * Exported so the guard can assert the footer offers exactly the sources
 * ReviewsJet fetches server-side — an extension-only source in a list beside
 * Google would present the two imports as the same thing, which the brief is
 * explicit about not doing.
 */
export const FOOTER_SOURCE_IDS: ReviewSourceId[] = CONNECT_SOURCES.map(
  (source) => source.id
);
